import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft, ShieldCheck, KeyRound } from "lucide-react";

export default function ResetPassword() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState(params.get("email") || "");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const e = params.get("email");
        if (e && !email) setEmail(e);
    }, [params, email]);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !code.trim() || !newPassword) return;
        if (code.length !== 6 || !/^\d{6}$/.test(code)) {
            toast.error("Code must be 6 digits.");
            return;
        }
        if (newPassword.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error("Passwords don't match.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", {
                email: email.trim(),
                code: code.trim(),
                new_password: newPassword,
            });
            toast.success("Password updated. Please log in with your new password.");
            nav("/login");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't reset password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-16 sm:py-24">
                <div className="max-w-md mx-auto px-4">
                    <Button asChild variant="ghost" className="mb-4 text-[#8b8ba0] hover:text-white">
                        <Link to="/forgot-password" className="inline-flex items-center gap-1" data-testid="reset-back-link">
                            <ArrowLeft className="w-4 h-4" /> Request new code
                        </Link>
                    </Button>
                    <div className="flex items-center gap-3 mb-6">
                        <Logo size={36} />
                        <div>
                            <p className="text-xs font-mono uppercase text-[#22d3ee] tracking-widest">Enter your code</p>
                            <h1 className="font-display text-3xl tracking-widest">RESET PASSWORD</h1>
                        </div>
                    </div>
                    <Card className="pp-bg-1 border-[#242436] text-white">
                        <CardContent className="p-6">
                            <p className="text-sm text-[#b7b7c6] mb-4">
                                Check your email for the 6-digit code. Enter it below along with your new password.
                            </p>
                            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                                <div>
                                    <Label htmlFor="email" className="text-[#b7b7c6]">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@email.com"
                                        className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]"
                                        data-testid="reset-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="code" className="text-[#b7b7c6]">6-Digit Code</Label>
                                    <Input
                                        id="code"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{6}"
                                        maxLength={6}
                                        required
                                        autoFocus={!!email}
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        placeholder="000000"
                                        className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72] font-mono text-2xl tracking-[0.4em] text-center"
                                        data-testid="reset-code-input"
                                    />
                                    <p className="mt-1 text-[10px] font-mono text-[#8b8ba0]">Expires 15 min after it was sent</p>
                                </div>
                                <div>
                                    <Label htmlFor="new-password" className="text-[#b7b7c6]">New password</Label>
                                    <Input
                                        id="new-password"
                                        type="password"
                                        required
                                        minLength={6}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]"
                                        data-testid="reset-new-password-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="confirm-password" className="text-[#b7b7c6]">Confirm password</Label>
                                    <Input
                                        id="confirm-password"
                                        type="password"
                                        required
                                        minLength={6}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="mt-1 pp-bg-0 border-[#242436] text-white placeholder:text-[#5e5e72]"
                                        data-testid="reset-confirm-password-input"
                                    />
                                </div>
                                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#22c55e] hover:brightness-110 text-[#07130b] font-semibold" data-testid="reset-submit-button">
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Update Password</span>}
                                </Button>
                            </form>
                            <p className="mt-4 text-sm text-[#b7b7c6] text-center">
                                Didn't get a code?{" "}
                                <Link to="/forgot-password" className="text-[#22d3ee] hover:underline">Request a new one</Link>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </section>
            <Footer />
        </div>
    );
}
